/**
 * Mass Role - Convert Pillar
 *
 * Conversão entre unidades de massa usando arquitetura hub-and-spoke.
 * Base: kilogram (kg)
 *
 * @example
 * import { convertMass, toBaseMass, fromBaseMass } from '@attrx/role-morphic/mass/convert';
 *
 * convertMass('kilogram', 'pound', 1);     // 2.20462...
 * toBaseMass('pound', 1);                  // 0.45359237
 * fromBaseMass('pound', 0.45359237);       // 1
 */

import { MASS_UNITS, type MassUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const MASS_BASE: MassUnit = "kilogram";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (kilogram)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em kilogram
 *
 * @example
 * toBaseMass('pound', 1);     // 0.45359237
 * toBaseMass('gram', 1000);   // 1
 */
export function toBaseMass(variant: MassUnit, value: number): number {
  const unit = MASS_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown mass variant: ${variant}`);
  }

  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (kilogram) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em kilogram
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseMass('pound', 1);        // 2.20462...
 * fromBaseMass('gram', 1);         // 1000
 */
export function fromBaseMass(variant: MassUnit, baseValue: number): number {
  const unit = MASS_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown mass variant: ${variant}`);
  }

  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de massa
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertMass('kilogram', 'pound', 1);     // 2.20462...
 * convertMass('pound', 'kilogram', 2.2);   // ~1
 * convertMass('gram', 'milligram', 1);     // 1000
 */
export function convertMass(
  from: MassUnit,
  to: MassUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base → to
  const baseValue = toBaseMass(from, value);
  return fromBaseMass(to, baseValue);
}

/**
 * Versão safe de convertMass que retorna Result
 *
 * @example
 * tryConvertMass('kilogram', 'pound', 1);
 * // { ok: true, value: 2.20462... }
 *
 * tryConvertMass('invalid', 'pound', 1);
 * // { ok: false, error: 'Unknown mass variant: invalid' }
 */
export function tryConvertMass(
  from: MassUnit,
  to: MassUnit,
  value: number
): Result<number> {
  try {
    const result = convertMass(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasMassVariant(variant: string): variant is MassUnit {
  return variant in MASS_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getMassVariants(): MassUnit[] {
  return Object.keys(MASS_UNITS) as MassUnit[];
}
